import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { EmployeeService } from './services/employee.service';
import { LocationService } from '../services/location.service';
import { EmployeeStats } from './models/employee.models';
import { LocationOption } from '../shared/types';
import { TabItem, TabHeaderComponent } from '../shared/components/tab-header/tab-header.component';
import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import { StatsCardComponent } from '../shared/components/stats-card/stats-card.component';
import { buildEmployeeStats } from './utils/employee-stats.utils';
import { EmployeeListTabComponent } from './components/employee-list-tab/employee-list-tab.component';
import { ShiftsTabComponent } from './components/shifts-tab/shifts-tab.component';
import { ShiftScheduleTabComponent } from './components/shift-schedule-tab/shift-schedule-tab.component';
import { RewardsTabComponent } from './components/rewards-tab/rewards-tab.component';
import { CashReconciliationComponent } from './components/cash-reconciliation/cash-reconciliation.component';

/**
 * Employee management dashboard component that organizes employee-related views into tabs, loads location options and summary statistics on init, and coordinates child tab interactions such as refreshing data and opening the add-employee modal.
 * @component
 * @selector app-employee
 * @example
 *   <app-employee></app-employee>
 * @input none
 * @output none
 * @method setActiveTab(tab: string) - Sets the active tab and triggers lazy loading for tab-specific child components.
 * @method onAddEmployee() - Switches to the Employees tab if needed and opens the add-employee modal after the view is ready.
 * @method onAddEmployeeModalClosed() - Closes the add-employee modal.
 * @method onStatsRefreshNeeded() - Reloads employee statistics when the employee list changes.
 * @method onShiftChanged() - Requests the Shift Schedule tab to reload after a shift is saved or deleted.
 * @method onScheduleChanged() - Reloads employee statistics after a schedule is created or updated.
 * @lifecycle ngOnInit() - Loads location options and employee statistics when the component initializes.
 * @lifecycle ngOnDestroy() - Cleans up subscriptions by completing the destroy notifier.
 */
@Component({
  selector: 'app-employee',
  standalone: true,
  // ViewEncapsulation.None makes parent SCSS apply globally so tab sub-components
  // (rendered without their own SCSS) inherit all existing styles without ::ng-deep.
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    TitleBarComponent,
    StatsCardComponent,
    TabHeaderComponent,
    EmployeeListTabComponent,
    ShiftsTabComponent,
    ShiftScheduleTabComponent,
    RewardsTabComponent,
    CashReconciliationComponent,
  ],
  templateUrl: './employee.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./employee.component.scss'],
})
export class EmployeeComponent implements OnInit, OnDestroy {
  @ViewChild(EmployeeListTabComponent) employeeListTab!: EmployeeListTabComponent;
  @ViewChild(ShiftsTabComponent) shiftsTab!: ShiftsTabComponent;
  @ViewChild(ShiftScheduleTabComponent) shiftScheduleTab!: ShiftScheduleTabComponent;

  private destroy$ = new Subject<void>();

  activeTab = 'employees';
  locationOptions: LocationOption[] = [];
  employeeStats: EmployeeStats[] = buildEmployeeStats(0, 0, 0);
  showAddEmployeeModal = false;

  tabItems: TabItem[] = [
    { id: 'employees', label: 'Employees' },
    { id: 'shifts', label: 'Shifts' },
    { id: 'shift-schedule', label: 'Shift Schedule' },
    { id: 'rewards', label: 'Rewards' },
    { id: 'cash-reconciliation', label: 'Cash Reconciliation' },
  ];

  constructor(
    private employeeService: EmployeeService,
    private locationService: LocationService,
    // Angular 22 runs this app zoneless: state set inside an HTTP callback or a
    // setTimeout never triggers a repaint on its own. Found via automated testing —
    // the stats cards stayed on their zero-value defaults after the real fetch had
    // already resolved.
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.locationService.clearShopLocation();
    this.loadLocationOptions();
    this.loadEmployeeStats();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    // Trigger data load in newly activated tab
    setTimeout(() => {
      if (tab === 'shifts') this.shiftsTab?.load();
      if (tab === 'shift-schedule') this.shiftScheduleTab?.load();
      // rewards tab loads in its own ngOnInit when mounted via *ngIf
      // employees tab auto-loads when it enters the DOM via *ngIf
      this.cdr.markForCheck();
    }, 0);
  }

  onAddEmployee(): void {
    if (this.activeTab !== 'employees') this.activeTab = 'employees';
    // Small delay so the tab component renders before showing modal
    setTimeout(() => {
      this.showAddEmployeeModal = true;
      this.cdr.markForCheck();
    }, 50);
  }

  onAddEmployeeModalClosed(): void {
    this.showAddEmployeeModal = false;
  }

  /** Called when employee list changes — reload stats cards. */
  onStatsRefreshNeeded(): void {
    this.loadEmployeeStats();
  }

  /** Called when a shift is saved/deleted — tell shift-schedule tab to reload. */
  onShiftChanged(): void {
    this.shiftScheduleTab?.reloadAfterShiftChange();
  }

  /** Called when a schedule is created/edited — reload stats. */
  onScheduleChanged(): void {
    this.loadEmployeeStats();
  }

  private loadLocationOptions(): void {
    this.locationService.loadLocations();
    this.locationService.locations$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.locationOptions = this.locationService.getLocationOptions();
        this.cdr.markForCheck();
      },
      error: () => {
        this.locationOptions = [];
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Loads employee statistics from the employee service and updates the component state.
   * @example
   * loadEmployeeStats()
   * void
   * @returns {void} Updates the employeeStats property with fetched or fallback values.
   */
  private loadEmployeeStats(): void {
    this.employeeService
      .getEmployeeStatistics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.status && response.data) {
            const d = response.data;
            this.employeeStats = buildEmployeeStats(
              parseInt(d.total_employees) || 0,
              parseInt(d.active_employees) || 0,
              parseInt(d.employees_on_leave_today) || 0
            );
          } else {
            this.employeeStats = buildEmployeeStats(0, 0, 0);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.employeeStats = buildEmployeeStats(0, 0, 0);
          this.cdr.markForCheck();
        },
      });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
